import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { Schema } from "effect";

import ThreadSidebar from "./Sidebar";
import SidebarV2 from "./sidebar/v2/SidebarV2";
import { useLegacySidebarEnabled } from "~/hooks/useSettings";
import { SidebarBrandHeader } from "./sidebar/SidebarBrandHeader";
import { WorkspaceStartupOverlay } from "./WorkspaceStartupOverlay";
import { useWorkspaceStartupShell, WORKSPACE_STARTUP_EXIT_MS } from "./workspaceStartupShell";
import { AppTopBar } from "./AppTopBar";
import {
  APP_SHELL_CONTENT_COLUMN_CLASS,
  APP_SHELL_MAIN_CLASS,
  APP_SHELL_ROW_CLASS,
} from "./appShellLayout";
import { Sidebar, SidebarProvider, SidebarRail, useOptionalSidebar } from "./ui/sidebar";
import { cn } from "~/lib/utils";
import { registerPrimarySidebarCloseHandler } from "./sidebar/primarySidebarBridge";
import { classifyEnvironmentId } from "../environments/environmentIdClassification";
import { getLocalStorageItem, removeLocalStorageItem } from "~/hooks/useLocalStorage";
import { useViewportWidth } from "~/hooks/useViewportWidth";
import {
  resolveInitialThreadSidebarWidth,
  resolveThreadSidebarMaximumWidth,
  THREAD_MAIN_CONTENT_MIN_WIDTH,
  THREAD_SIDEBAR_MIN_WIDTH,
  THREAD_SIDEBAR_WIDTH_STORAGE_KEY,
} from "./threadSidebarWidth";
import { isCmmdEmbedMode } from "../embed/cmmdEmbedMode";

const LEGACY_CMMD_PROJECT_PATH = /^\/projects\/\d+\/?$/;
const WINDOWS_RUNTIME_THREAD_PATH = /^\/runtime_[^/]+\/[^/]+\/?$/;

interface AppSidebarLayoutProps {
  readonly children: ReactNode;
}

/** Mount the responsive app shell with v2 by default, restoring the legacy sidebar when hydrated settings enable it. */
export function AppSidebarLayout({ children }: AppSidebarLayoutProps) {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const isThreadRoute = useParams({
    strict: false,
    select: (params) =>
      typeof params["environmentId"] === "string" && typeof params["threadId"] === "string",
  });
  // CMMD /forge owns the outer identity row (breadcrumb, Focus, Help, Gear).
  // Keep Forge's AppTopBar so page titles (Usage, Estimated value, etc.) stay
  // visible under that row. Hide only the sidebar brand header so the Forge
  // wordmark is not duplicated next to CMMD's Forge crumb. ThreadSidebar's
  // All projects chrome then shares the AppTopBar row.
  const hideSidebarBrandHeader = isCmmdEmbedMode();
  // The sidebar's ceiling is derived from the window, so it is a subscription:
  // a width dragged out on an external monitor has to be re-clamped when the
  // same window is reopened on a laptop.
  const viewportWidth = useViewportWidth();
  const legacySidebarEnabled = useLegacySidebarEnabled();
  const sidebarMaximumWidth = resolveThreadSidebarMaximumWidth(viewportWidth);
  const [sidebarWidth, setSidebarWidth] = useState(readInitialThreadSidebarWidth);
  // Resolved here rather than left to the stylesheet's `16rem`: Forge's root
  // font-size is user-adjustable down to 11px, where `16rem` is 176px — below
  // the 208px drag floor, so the sidebar would open under its own minimum and
  // jump on first drag.
  // Clamped on render, not only on drag: shrinking the window lowers the
  // ceiling, and the stored width has to yield to it without waiting for the
  // user to grab the handle. Storage keeps the larger number, so the sidebar
  // returns to it when the window grows back.
  const sidebarProviderStyle = {
    "--sidebar-width": `${Math.min(sidebarWidth, sidebarMaximumWidth)}px`,
  } as CSSProperties;
  const resetSidebarWidth = () => {
    try {
      removeLocalStorageItem(THREAD_SIDEBAR_WIDTH_STORAGE_KEY);
    } catch (error) {
      console.error("Could not clear persisted thread sidebar width.", error);
    }
    setSidebarWidth(resolveInitialThreadSidebarWidth(null, viewportWidth));
  };
  const workspaceShell = useWorkspaceStartupShell();
  const shouldBlockForWorkspaceStartup = shouldRouteWaitForWorkspaceStartup(pathname, {
    isThreadRoute,
  });
  const isAppChromeVisible =
    workspaceShell.isAppChromeVisible ||
    workspaceShell.isShellMounted ||
    !shouldBlockForWorkspaceStartup;

  useEffect(() => {
    const onMenuAction = window.desktopBridge?.onMenuAction;
    if (typeof onMenuAction !== "function") {
      return;
    }

    const unsubscribe = onMenuAction((action) => {
      if (action !== "open-settings") return;
      void navigate({ to: "/settings" });
    });

    return () => {
      unsubscribe?.();
    };
  }, [navigate]);

  return (
    <SidebarProvider defaultOpen style={sidebarProviderStyle}>
      <PrimarySidebarCloseBridge />
      {/* Row, not column: the sidebar runs the full height of the window so its
          brand header and the bar's breadcrumb share one horizontal line. The
          bar therefore belongs to the content column, not above both. */}
      <div
        className={cn(
          APP_SHELL_ROW_CLASS,
          "transition-opacity ease-[cubic-bezier(0.25,1,0.5,1)]",
          isAppChromeVisible ? "opacity-100" : "opacity-0",
        )}
        style={{ transitionDuration: `${WORKSPACE_STARTUP_EXIT_MS}ms` }}
        data-workspace-app-chrome-visible={isAppChromeVisible ? "true" : "false"}
      >
        <Sidebar
          side="left"
          collapsible="offcanvas"
          className="border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
          resizable={{
            maxWidth: sidebarMaximumWidth,
            minWidth: THREAD_SIDEBAR_MIN_WIDTH,
            // `nextWidth <= currentWidth` first, so shrinking is always legal.
            // Without it a sidebar that is already too wide — restored from a
            // wider window, or grown while the right panel was closed — fails
            // its own guard and cannot be dragged back, which reads as a frozen
            // handle rather than a constraint.
            shouldAcceptWidth: ({ currentWidth, nextWidth, wrapper }) =>
              nextWidth <= currentWidth ||
              wrapper.clientWidth - nextWidth >= THREAD_MAIN_CONTENT_MIN_WIDTH,
            storageKey: THREAD_SIDEBAR_WIDTH_STORAGE_KEY,
            onResize: setSidebarWidth,
          }}
        >
          {hideSidebarBrandHeader ? null : <SidebarBrandHeader />}
          {/* Sidebar v2 is the default; the legacy escape hatch (Settings →
              Appearance) restores forge's v1 per-project tree. V2 mounts before
              hydration; persisted legacy opt-ins then switch to v1. */}
          {legacySidebarEnabled ? <ThreadSidebar /> : <SidebarV2 />}
          <SidebarRail onDoubleClick={resetSidebarWidth} />
        </Sidebar>
        <div className={APP_SHELL_CONTENT_COLUMN_CLASS}>
          <AppTopBar />
          <div className={APP_SHELL_MAIN_CLASS} data-workspace-main-content="true">
            {children}
            {shouldBlockForWorkspaceStartup ? (
              <WorkspaceStartupOverlay shell={workspaceShell} />
            ) : null}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}

function readInitialThreadSidebarWidth(): number {
  if (typeof window === "undefined") return resolveInitialThreadSidebarWidth(null, 1280);
  try {
    return resolveInitialThreadSidebarWidth(
      getLocalStorageItem(THREAD_SIDEBAR_WIDTH_STORAGE_KEY, Schema.Finite),
      window.innerWidth,
    );
  } catch (error) {
    console.error("Could not read persisted thread sidebar width.", error);
    return resolveInitialThreadSidebarWidth(null, window.innerWidth);
  }
}

export function shouldRouteWaitForWorkspaceStartup(
  pathname: string,
  options: { readonly isThreadRoute?: boolean } = {},
): boolean {
  // Runtime admission gates execution, while the thread owns authorized history loading.
  if (
    options.isThreadRoute === true ||
    pathname === "/new" ||
    pathname === "/new/" ||
    pathname.startsWith("/draft/")
  ) {
    return false;
  }
  const routeEnvironmentSegment = pathname.split("/")[1] ?? "";
  let routeEnvironmentId = routeEnvironmentSegment;
  try {
    routeEnvironmentId = decodeURIComponent(routeEnvironmentSegment);
  } catch {
    // A malformed escape cannot be a canonical CMMD runtime route.
  }
  return !(
    // `/project/:id` is the project page; `/projects/:id` and
    // `/cmmd/project/:id` are redirect shims for saved links. All three must
    // skip the startup gate. The trailing slash on "/project/" is load-bearing:
    // without it the check would also swallow "/projects" as a prefix.
    (
      LEGACY_CMMD_PROJECT_PATH.test(pathname) ||
      pathname.startsWith("/project/") ||
      pathname.startsWith("/cmmd/project/") ||
      pathname === "/usage" ||
      pathname === "/usage/" ||
      pathname === "/settings" ||
      pathname.startsWith("/settings/") ||
      pathname === "/automations" ||
      pathname.startsWith("/automations/") ||
      pathname === "/plugins" ||
      pathname.startsWith("/plugins/") ||
      pathname === "/pull-requests" ||
      WINDOWS_RUNTIME_THREAD_PATH.test(pathname) ||
      classifyEnvironmentId(routeEnvironmentId).isCloud
    )
  );
}

function PrimarySidebarCloseBridge() {
  const sidebar = useOptionalSidebar();

  useEffect(() => {
    if (!sidebar) {
      return registerPrimarySidebarCloseHandler(null);
    }

    return registerPrimarySidebarCloseHandler(() => {
      if (sidebar.isMobile) {
        sidebar.setOpenMobile(false);
        return;
      }
      sidebar.setOpen(false);
    });
  }, [sidebar]);

  return null;
}
